import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteFieldCropPractice, deleteFieldCropPracticeAdmin } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  DELETE_FIELD_CROP_PRACTICE,
  GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_COVER_CROP_DETECTIONS,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_RESILIENCE_SCORE,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
  GET_VERIFICATION_EDITING_REQUESTS,
} from "consts";

type MutateParams = {
  fieldId: string;
  cropId: string;
};

export const useDeleteFieldCropPractice = ({ fieldId, farmId }: { fieldId: string; farmId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DELETE_FIELD_CROP_PRACTICE, fieldId],
    mutationFn: ({ cropId }: { cropId: string }) => mutate({ cropId, fieldId }),
    onSettled: () => invalidateAfterDeletion(queryClient, [fieldId], farmId),
  });
};

export const useBulkDeleteFieldCropPractices = ({ farmId }: { farmId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DELETE_FIELD_CROP_PRACTICE],
    mutationFn: (crops: MutateParams[]) => Promise.all(crops.map(mutate)),
    onSettled: (_data, _error, crops) => invalidateAfterDeletion(queryClient, Array.from(new Set(crops.map((crop) => crop.fieldId))), farmId),
  });
};

const invalidateAfterDeletion = (queryClient: QueryClient, fieldIds: string[], farmId?: string) => {
  for (const fieldId of fieldIds) {
    invalidateCarbonProgramQueriesByChunk(fieldId);
    invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
    queryClient.refetchQueries({ queryKey: [GET_FIELD_PRACTICES, fieldId] });
    queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
  }

  queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });
  queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
  queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });

  if (farmId) {
    queryClient.invalidateQueries({ queryKey: [GET_FARM_RESILIENCE_SCORE, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
    // Deleting a cover crop created from a satellite suggestion also deletes its detection
    queryClient.invalidateQueries({ queryKey: [GET_FARM_COVER_CROP_DETECTIONS, farmId] });
  }
};

const mutate = ({ fieldId, cropId }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return deleteFieldCropPracticeAdmin({ fieldId, cropId });
  }

  return deleteFieldCropPractice({ fieldId, cropId });
};
